import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Play, ExternalLink, ArrowUpRight } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FALLBACK_EPISODES = [
  { id: "8", title: "Why Do We Work to Be Accepted?", description: "When did you first feel that simply being yourself was not enough? Steve sits down with Maria, a Savi resident and community builder, for an honest conversation about belonging, addiction, hospitality, achievement and the exhausting need to be accepted.", youtube_id: "PN1fgH0xrAk", duration: "", episode_number: 8 },
  { id: "7", title: "From Corporate Highs to Rock Bottom", description: "She didn't look like an addict. Good job. Smart. Put together. Episode 7 with Kat from Toronto is one of those conversations that's hard to listen to… because it's honest. No dramatic rock bottom story. Just the slow, quiet slide that most people don't even realise they're in.", youtube_id: "l7PqiskgmGw", duration: "60:19", episode_number: 7, special: true },
  { id: "6", title: "The Messages Changed Everything", description: "Sometimes one conversation flips everything on its head. This is that episode. Raw, real, and completely unscripted—the way chaos was meant to be.", youtube_id: "5VF_wUvgJXU", duration: "", episode_number: 6 },
  { id: "5", title: "Is This a Cult… or Just What Humans Are Missing?", description: "A conversation with Marina about community, belonging, and why intentional living makes people uncomfortable. Are we building something special or just weird?", youtube_id: "QX2P6s2RsEg", duration: "48:00", episode_number: 5 },
  { id: "4", title: "How I Became an Alcoholic and a Great Chef", description: "A raw, honest conversation about the intersection of addiction and success in the culinary world. From the highs of the kitchen to the lows of alcoholism.", youtube_id: "9sOZys_UEg8", duration: "52:06", episode_number: 4 },
  { id: "3", title: "Running a Sober Coliving Is Not What People Think", description: "What it's really like to build and run an intentional, sober community. The challenges, surprises, and unexpected lessons from Savi Coliving.", youtube_id: "75VXGN74oiI", duration: "38:02", episode_number: 3 },
  { id: "2", title: "Frogs Can't Become Enlightened", description: "A philosophical conversation about growth, change, and the limits of transformation. Sometimes the truth is funnier than fiction.", youtube_id: "tOE_FBurlwo", duration: "40:57", episode_number: 2 },
  { id: "1", title: "The Sniffles", description: "The very first episode. Where it all began—around a table, with time, and very little intention to behave. Welcome to the chaos.", youtube_id: "rnaUIW1F7ic", duration: "41:27", episode_number: 1 },
];

const THUMB_QUALITIES = ["maxresdefault", "sddefault", "hqdefault"];

const YtThumb = ({ ytid, alt, className, testId }) => {
  const [q, setQ] = useState(0);
  const downgrade = () => setQ((p) => Math.min(p + 1, THUMB_QUALITIES.length - 1));
  return (
    <img
      src={`https://img.youtube.com/vi/${ytid}/${THUMB_QUALITIES[q]}.jpg`}
      alt={alt}
      className={className}
      loading="lazy"
      onError={downgrade}
      onLoad={(e) => {
        if (e.target.naturalWidth <= 120) downgrade();
      }}
      data-testid={testId}
    />
  );
};

export const Episodes = () => {
  const [episodes, setEpisodes] = useState(FALLBACK_EPISODES);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/episodes`)
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setEpisodes(data);
        }
      })
      .catch(() => {});
  }, []);

  const latest = episodes[0];
  const rest = episodes.slice(1);

  return (
    <section id="episodes" className="px-5 sm:px-8 lg:px-12 pb-14 sm:pb-20 md:pb-32" data-testid="episodes-section">
      {/* Section header */}
      <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
        <div className="min-w-0">
          <p className="overline-tcc mb-4">The Archive</p>
          <h2 className="font-display text-4xl sm:text-6xl md:text-7xl leading-[0.9] text-[#F4F2EE] break-words">
            Every episode.<br />
            <span className="text-stroke">Zero polish.</span>
          </h2>
        </div>
        <a
          href="https://www.youtube.com/@TruthsChaosCupcakes"
          target="_blank"
          rel="noopener noreferrer"
          className="btn-brutal-ghost"
          data-testid="view-all-episodes-btn"
        >
          Full archive on YouTube
          <ExternalLink size={14} />
        </a>
      </div>

      {/* Featured latest episode */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7 }}
        className="grid lg:grid-cols-2 border border-[#232332] mb-16"
      >
        <div className="aspect-video bg-[#0C0C14] relative">
          {playing ? (
            <iframe
              src={`https://www.youtube.com/embed/${latest.youtube_id}?autoplay=1&rel=0`}
              title={latest.title}
              className="w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <button
              onClick={() => setPlaying(true)}
              className="group/play absolute inset-0 w-full h-full cursor-pointer overflow-hidden"
              aria-label={`Play ${latest.title}`}
              data-testid="featured-play-facade"
            >
              <YtThumb
                ytid={latest.youtube_id}
                alt={latest.title}
                className="w-full h-full object-cover group-hover/play:scale-[1.03] transition-transform duration-500"
                testId="featured-thumbnail"
              />
              <span className="absolute inset-0 bg-gradient-to-t from-[#08080D]/80 via-[#08080D]/10 to-[#08080D]/30" />
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="w-16 h-16 sm:w-20 sm:h-20 bg-[#FF3E8A] group-hover/play:bg-[#C8FF3E] flex items-center justify-center transition-colors duration-200">
                  <Play size={28} fill="#08080D" className="text-[#08080D] ml-1" />
                </span>
              </span>
              <span className="absolute bottom-4 left-4 font-mono-tcc text-[10px] uppercase tracking-[0.2em] text-[#F4F2EE]/80">
                ▸ Tap to play — EP.0{latest.episode_number}
              </span>
            </button>
          )}
        </div>
        <div className="p-7 sm:p-10 lg:p-12 flex flex-col justify-center border-t lg:border-t-0 lg:border-l border-[#232332]">
          <div className="flex flex-wrap items-center gap-3 sm:gap-4 mb-6">
            <span
              className={`font-mono-tcc text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 ${
                latest.special ? "bg-[#FF3E8A] text-[#08080D]" : "bg-[#C8FF3E] text-[#08080D]"
              }`}
            >
              {latest.special ? "Special episode" : "Latest drop"}
            </span>
            <span className="font-mono-tcc text-xs text-[#9494A6]">EP.0{latest.episode_number}</span>
            {latest.duration && (
              <span className="font-mono-tcc text-xs text-[#5E5E72]">{latest.duration}</span>
            )}
          </div>
          <h3 className="font-display text-3xl md:text-4xl leading-none text-[#F4F2EE] mb-5 break-words">
            {latest.title}
          </h3>
          <p className="text-[#9494A6] text-sm md:text-base font-light leading-relaxed mb-8 line-clamp-4">
            {latest.description}
          </p>
          <a
            href={`https://www.youtube.com/watch?v=${latest.youtube_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-brutal-pink w-fit"
            data-testid="watch-latest-episode"
          >
            <Play size={14} fill="currentColor" />
            Watch now
          </a>
        </div>
      </motion.div>

      {/* Episode index list */}
      <div className="border-t border-[#232332]">
        {rest.map((episode, index) => (
          <motion.a
            key={episode.id}
            href={`https://www.youtube.com/watch?v=${episode.youtube_id}`}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.45, delay: Math.min(index * 0.06, 0.3) }}
            className="group flex items-center gap-4 md:gap-8 py-5 md:py-8 border-b border-[#232332] hover:bg-[#0D0D15] transition-colors duration-200 px-1 md:px-4"
            data-testid={`episode-card-${episode.episode_number}`}
          >
            {/* Number — desktop only */}
            <span className="hidden md:block font-display text-6xl text-stroke group-hover:text-stroke-pink transition-colors duration-200 shrink-0 w-24">
              {String(episode.episode_number).padStart(2, "0")}
            </span>

            {/* Thumbnail */}
            <div className="relative w-32 sm:w-44 md:w-56 shrink-0 aspect-video overflow-hidden border border-[#232332]">
              <YtThumb
                ytid={episode.youtube_id}
                alt={episode.title}
                className="w-full h-full object-cover img-brutal group-hover:scale-105 transition-transform duration-500"
              />
              <span
                className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                data-testid={`play-episode-${episode.episode_number}`}
              >
                <Play size={26} fill="#F4F2EE" className="text-[#F4F2EE]" />
              </span>
            </div>

            {/* Title + desc */}
            <div className="min-w-0 flex-1">
              <p className="md:hidden font-mono-tcc text-[10px] uppercase tracking-[0.2em] text-[#FF3E8A] mb-1.5">
                EP.{String(episode.episode_number).padStart(2, "0")}
                {episode.duration && <span className="text-[#5E5E72] ml-3">{episode.duration}</span>}
              </p>
              <h3 className="font-display text-lg sm:text-xl md:text-2xl leading-tight text-[#F4F2EE] group-hover:text-[#FF3E8A] transition-colors duration-200 line-clamp-2 break-words">
                {episode.title}
              </h3>
              <p className="hidden md:block text-[#9494A6] text-sm font-light leading-relaxed line-clamp-2 mt-2 max-w-xl">
                {episode.description}
              </p>
            </div>

            {/* Duration + arrow */}
            <div className="hidden md:flex items-center justify-end gap-4 shrink-0">
              {episode.duration && (
                <span className="font-mono-tcc text-xs text-[#5E5E72]">{episode.duration}</span>
              )}
              <ArrowUpRight
                size={20}
                className="text-[#9494A6] group-hover:text-[#C8FF3E] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-200"
              />
            </div>
          </motion.a>
        ))}
      </div>
    </section>
  );
};
